import PropTypes from 'prop-types';
import 'moment/locale/it';
import '../../css/tempo.scss';
import * as Yup from 'yup';
import { Button, Spinner } from 'react-bootstrap';
import { Form as FormikForm, Formik } from 'formik';
import FormCheckbox from '../../components/Form/FormCheckbox';
import FormItem from '../../components/Form/FormItem';
import { SpeakerPartecipantSelector } from './SpeakerPartecipantSelector';
import { addTrainingDay, updateTrainingDay } from '../../utils/API/budgets';
import { useContext, useState } from 'react';
import moment from 'moment';
import { EmployeeListContext } from '../../context/EmployeeListContext';
import { success, error } from '../../utils/notification';
import FormDatePicker from '../../components/Form/FormDatePicker';

const initialValues = (currentData) => ({
  id: currentData?.id || undefined,
  date:
    currentData?.date || currentData?.date_id
      ? moment(currentData?.date || currentData?.date_id, 'YYYY-MM-DD')
      : moment(),
  description: currentData?.description || '',
  duration: currentData?.duration || 1,
  useDayOff: currentData?.useDayOff || false,
});

const validationSchema = Yup.object().shape({
  date: Yup.date().required('Campo obbligatorio'),
  description: Yup.string()
    .required('Campo obbligatorio')
    .min(5, 'Lunghezza minima 5 caratteri')
    .max(255, 'Lunghezza massima 255 caratteri'),
  duration: Yup.number()
    .required('Campo obbligatorio')
    .positive("Valore non ammesso, dev'essere maggiore di 0"),
});

export const DaysForm = ({ close, currentData, updateData, company }) => {
  const isNew = !currentData?.id;

  const { employeesByIdQueryA: employeesById } = useContext(EmployeeListContext);

  // Convert participants and speakers from objects/usernames to IDs
  const convertToIds = (items) => {
    if (!items || !items.length) return [];
    // If already numbers, return as is
    if (typeof items[0] === 'number') return items;
    // If objects with id property
    if (typeof items[0] === 'object' && items[0].id) {
      return items.map((item) => item.id);
    }
    // If usernames (strings), find by username
    if (typeof items[0] === 'string') {
      return items
        .map(
          (username) =>
            Object.values(employeesById).find(
              (emp) => emp.username === username
            )?.id
        )
        .filter((id) => id !== undefined);
    }
    return [];
  };

  const [participants, setParticipants] = useState(
    convertToIds(currentData?.participants ?? [])
  );
  const [speakers, setSpeakers] = useState(
    convertToIds(currentData?.speakers ?? [])
  );
  const hasMembers = participants.length + speakers.length > 0;

  async function onSubmit(values, { setSubmitting, resetForm, setFieldValue }) {
    const { id, createAnother } = values;
    const p = participants
      .map((userId) => employeesById[userId]?.username)
      .filter((u) => u);
    const s = speakers
      .map((userId) => employeesById[userId]?.username)
      .filter((u) => u);
    const data = { ...values, date: values.date.format('YYYY-MM-DD') };
    try {
      if (id) {
        await updateTrainingDay(data, p, s);
      } else {
        await addTrainingDay(data, p, s);
      }
      success('Esito', 'Operazione completata con successo!');
      updateData();
      if (!createAnother) {
        close();
      } else {
        resetForm();
        setParticipants([]);
        setSpeakers([]);
        setFieldValue('createAnother', createAnother);
      }
    } catch (e) {
      if (e.response.status === 412) {
        const errorData = e.response.data.map(
          (i) => `${i.user} (${i.total_spent}/${i.budget})`
        );
        error(
          'Attenzione!',
          `Operazione fallita perché i seguenti utenti non hanno disponibilità di giorni: ${errorData.join(
            ' - '
          )}`
        );
      } else if (e.response.status === 422) {
        error(
          'Attenzione!',
          `Il flag Ferie può essere abilitato solo quando viene specificato un unico partecipante.`
        );
      } else {
        error('Errore!', 'Operazione fallita!');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Formik
      validationSchema={validationSchema}
      onSubmit={async (values, actions) => {
        await onSubmit(values, actions, close, updateData);
      }}
      initialValues={initialValues(currentData)}
    >
      {({ handleSubmit, isSubmitting, isValid }) => {
        const buttonText = currentData?.id ? 'Aggiorna' : 'Inserisci';

        return (
          <FormikForm noValidate onSubmit={handleSubmit}>
            <FormDatePicker name="date" label="Data *" />
            <FormItem
              type="textarea"
              name="description"
              rows="1"
              label="Descrizione *"
            />
            <FormItem
              type="number"
              pattern="[0-9]+([\.,][0-9]{1,2})?"
              name="duration"
              label="Durata (giorni) *"
            />
            <FormCheckbox name="useDayOff" label="Ferie" />
            <SpeakerPartecipantSelector
              participantsName="participants"
              participants={participants}
              setParticipants={setParticipants}
              speakersName="speakers"
              speakers={speakers}
              setSpeakers={setSpeakers}
              company={company}
            />
            {isNew && (
              <>
                <hr />
                <FormCheckbox
                  name="createAnother"
                  label="Inserisci subito un altro"
                />
              </>
            )}
            <div className="buttonContent">
              <Button
                variant="secondary"
                onClick={close}
                disabled={isSubmitting}
              >
                Annulla
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={isSubmitting || !isValid || !hasMembers}
              >
                {isSubmitting ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      style={{ marginRight: '8px' }}
                    />
                    {buttonText}
                  </>
                ) : (
                  buttonText
                )}
              </Button>
            </div>
          </FormikForm>
        );
      }}
    </Formik>
  );
};

DaysForm.propTypes = {
  close: PropTypes.func.isRequired,
  currentData: PropTypes.shape({
    id: PropTypes.number,
    date: PropTypes.string,
    date_id: PropTypes.string,
    description: PropTypes.string,
    duration: PropTypes.number,
    // eslint-disable-next-line react/forbid-prop-types
    participants: PropTypes.array,
    // eslint-disable-next-line react/forbid-prop-types
    speakers: PropTypes.array,
    useDayOff: PropTypes.bool,
  }).isRequired,
  updateData: PropTypes.func.isRequired,
  company: PropTypes.number.isRequired,
};
